/**
 * @file PreviewDrawer - 通用底部预览抽屉容器
 *
 * 封装统一的 Drawer 配置（底部弹出、模糊遮罩、圆角顶部），
 * 提供可选侧边栏 + 主内容区域的布局。
 */

import React from 'react';
import type {DrawerProps} from 'antd';
import {createCx} from '@utils/cx';
import styles from './index.module.less';
import Drawer from '@/components/common/Drawer';
import ErrorBoundary from '@/components/common/ErrorBoundary';

const cx = createCx(styles);

export interface PreviewDrawerProps extends Omit<DrawerProps, 'title' | 'placement' | 'children'> {
    /** 侧边栏内容 */
    sidebar?: React.ReactNode;
    /** 主内容区域 */
    children: React.ReactNode;
}

const PreviewDrawer: React.FC<PreviewDrawerProps> = ({
    open,
    onClose,
    sidebar,
    children,
    closable = true,
    rootClassName,
    ...restProps
}) => {
    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={null}
            placement="bottom"
            closable={false}
            destroyOnHidden
            rootClassName={cx('drawer', rootClassName)}
            styles={{
                mask: {
                    backdropFilter: 'blur(5px)',
                    backgroundColor: 'var(--color-bg-mask-60)',
                },
                wrapper: {height: 'calc(100vh - 80px)'},
                section: {borderRadius: '16px 16px 0 0', overflow: 'hidden'},
                body: {padding: 0, overflow: 'hidden'},
            }}
            {...restProps}
        >
            <div className={cx('container')}>
                <ErrorBoundary>
                    {sidebar}
                    <main className={cx('main')}>{children}</main>
                </ErrorBoundary>
            </div>
            {closable && (
                <button className={cx('closeBtn')} onClick={onClose}>
                    <span className={cx('closeIcon')} />
                </button>
            )}
        </Drawer>
    );
};

export default PreviewDrawer;
